/**
 * SCRUM-207 — /market/:slug/arrepentimiento
 * Botón de Arrepentimiento (Disp. 954/2025 + 3/2026).
 * Formulario público, no requiere login.
 */
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { useState } from "react";
import { loadMarketCatalog, type MarketCatalogResult } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { MarketStoreHeader } from "@/components/market/MarketChrome";
import { MarketFooter } from "@/components/market/MarketFooter";
import { projectExperience } from "@/lib/experience-definition/project";
import { MarketPageSkeleton } from "@/components/market/MarketSkeletons";
import { CheckCircle2, ArrowLeft, Loader2 } from "lucide-react";

const fetchCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }) => {
    return loadMarketCatalog(slug, { request: getRequest() });
  });

export const Route = createFileRoute("/market_/$slug/arrepentimiento")({
  pendingComponent: MarketPageSkeleton,
  pendingMs: 0,
  loader: async ({ params }) => fetchCatalog({ data: params.slug }),
  component: ArrepentimientoRoute,
  head: ({ params, loaderData }) => {
    const result = loaderData as MarketCatalogResult | undefined;
    const name = result?.ok ? result.data.tenant.name : params.slug;
    return {
      meta: [
        { title: `Botón de Arrepentimiento — ${name}` },
        { name: "description", content: `Ejercé tu derecho de arrepentimiento en ${name}` },
        { name: "viewport", content: "width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover" },
      ],
    };
  },
});

const labelClass = "mb-1.5 block text-sm font-medium text-[var(--market-on-surface)]";
const fieldClass =
  "h-11 w-full rounded-xl border border-[var(--market-outline-variant)] bg-[var(--market-surface-bright)] px-3.5 text-sm text-[var(--market-on-surface)] outline-none transition-shadow focus:border-[var(--market-accent)] focus:ring-2 focus:ring-[var(--market-accent)]/25 placeholder:text-[var(--market-on-surface-variant)]/50";

type FormState = "form" | "submitting" | "success";

function ArrepentimientoRoute() {
  const { slug } = Route.useParams();
  const result = Route.useLoaderData() as MarketCatalogResult;

  const offline = renderMarketPublicOffline(result);
  if (offline) return offline;
  if (!result.ok) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[#0a1620] p-6 text-center text-white">
        <p className="text-sm text-white/70">Página no disponible.</p>
      </div>
    );
  }

  const { data } = result;
  const published = data.config.experiencePublished;
  const doc = published ? projectExperience(published as never) : null;
  const tenantName = data.tenant.name;
  const logoUrl = doc?.header?.logoUrl ?? data.config.logoUrl ?? null;

  const [state, setState] = useState<FormState>("form");
  const [error, setError] = useState<string | null>(null);
  const [refCode, setRefCode] = useState<string>("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [doc_, setDoc] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [reason, setReason] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setState("submitting");
    try {
      const res = await fetch(`/api/market/${slug}/consumer-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "ARREPENTIMIENTO",
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          doc: doc_.trim() || undefined,
          orderNumber: orderNumber.trim() || undefined,
          reason: reason.trim() || undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        setError(json.error ?? "Error al enviar la solicitud");
        setState("form");
        return;
      }
      setRefCode(json.referenceCode);
      setState("success");
    } catch {
      setError("Error de conexión. Intentá de nuevo.");
      setState("form");
    }
  }

  return (
    <div className="market-surface flex min-h-dvh flex-col">
      <MarketStoreHeader tenantName={tenantName} logoUrl={logoUrl} cartCount={0} cartTotal={0} />

      <main className="mx-auto w-full max-w-xl flex-1 px-4 py-8 sm:py-12">
        {state === "success" ? (
          <div className="rounded-2xl border border-[var(--market-outline-variant)] bg-[var(--market-surface-bright)] p-6 text-center sm:p-8">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h2 className="mb-2 text-xl font-bold text-[var(--market-on-surface)]">
              Solicitud registrada
            </h2>
            <p className="mb-4 text-sm text-[var(--market-on-surface-variant)]">
              Tu solicitud de arrepentimiento fue recibida correctamente.
            </p>
            <div className="mb-4 rounded-xl bg-[var(--market-surface)] p-4">
              <p className="text-xs text-[var(--market-on-surface-variant)]">Código de trámite</p>
              <p className="mt-1 font-mono text-lg font-bold tracking-wider text-[var(--market-accent)]">
                {refCode}
              </p>
            </div>
            <p className="text-xs leading-relaxed text-[var(--market-on-surface-variant)]">
              Guardá este código para consultar el estado de tu solicitud. El comercio procesará
              tu pedido y gestionará el reembolso a través de su cuenta de pagos. Recibirás
              novedades en tu email.
            </p>
            <a
              href={`/market/${slug}`}
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[var(--market-accent)] transition-colors hover:underline"
            >
              <ArrowLeft className="h-4 w-4" /> Volver a la tienda
            </a>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-[var(--market-on-surface)] sm:text-3xl">
                Botón de Arrepentimiento
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-[var(--market-on-surface-variant)]">
                Si realizaste una compra y querés ejercer tu derecho de arrepentimiento dentro del
                plazo legal (10 días corridos), completá el formulario. El comercio recibirá tu
                solicitud y gestionará la devolución del pago a través de su propia cuenta.
              </p>
              <p className="mt-1 text-xs text-[var(--market-on-surface-variant)]">
                Disposición 954/2025 — Secretaría de Comercio Interior.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={labelClass}>Nombre completo *</label>
                <input
                  type="text"
                  required
                  minLength={2}
                  maxLength={200}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Juan Pérez"
                  className={fieldClass}
                />
              </div>

              <div>
                <label className={labelClass}>Email *</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className={fieldClass}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Teléfono</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+54 11 1234-5678"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>DNI / CUIT</label>
                  <input
                    type="text"
                    value={doc_}
                    onChange={(e) => setDoc(e.target.value)}
                    placeholder="12345678"
                    className={fieldClass}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Número de pedido</label>
                <input
                  type="text"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="Ej: 1042"
                  className={fieldClass}
                />
                <p className="mt-1 text-xs text-[var(--market-on-surface-variant)]">
                  Si lo tenés, ingresá el número de pedido para agilizar el trámite.
                </p>
              </div>

              <div>
                <label className={labelClass}>Motivo</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={2000}
                  rows={3}
                  placeholder="Contanos por qué querés ejercer tu derecho de arrepentimiento"
                  className={`${fieldClass} h-auto py-2.5`}
                />
              </div>

              {error ? (
                <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={state === "submitting"}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[var(--market-accent)] font-semibold text-[var(--market-on-primary,#fff)] transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {state === "submitting" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Enviando…
                  </>
                ) : (
                  "Enviar solicitud"
                )}
              </button>
            </form>
          </>
        )}
      </main>

      <MarketFooter
        tenantName={tenantName}
        description={data.tenant.description}
        logoUrl={logoUrl}
        phone={data.tenant.phone}
        whatsappNumber={data.config.whatsappNumber}
        locations={data.locations}
        categories={data.categories}
        socialLinks={data.socialLinks}
        footer={doc?.footer}
        storeSlug={slug}
        showReservations={data.showReservations}
        showServiceBookings={data.showServiceBookings}
        showB2bPortal={data.showB2bPortal}
        activeB2bChannel={data.activeB2bChannel}
        showMerchantPersonalization={data.merchantEnabled}
        showMap={doc?.footer?.showMap ?? true}
      />
    </div>
  );
}
